/**
 * SHA-256 of a file's raw bytes as lowercase hex. Matches the backend's stored
 * document `content_hash` (hashlib.sha256 over the same bytes), so the two can
 * be compared directly to tell a re-upload from a different file that happens
 * to share a name.
 */
export async function sha256Hex(file: Blob): Promise<string> {
	const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
	return Array.from(new Uint8Array(digest))
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('');
}

/**
 * Decimal, not 1024² — these numbers are read against disk and network throughput, which vendors
 * quote decimally, and against the size Finder shows for the same file, which has been decimal
 * since macOS 10.6. The size *limit* messages elsewhere divide by 1024² instead, and are right to:
 * they render a ceiling that was defined in those units. Do not unify the two.
 */
const BYTES_PER_MEGABYTE = 1_000_000;
const MS_PER_SECOND = 1000;

const megabytesOf = (bytes: number): string => (bytes / BYTES_PER_MEGABYTE).toFixed(1);

/** Above this, one file's hash is worth naming on its own — it is the one holding the drop up. */
export const SLOW_FILE_HASH_MS = 500;

/**
 * Throughput is the number worth reading: hashing runs at hundreds of MB/s off a local disk, so a
 * rate far below that means the bytes are not local — a cloud-synced placeholder being downloaded
 * on demand — rather than the digest being slow.
 */
export const formatHashPassSummary = (fileCount: number, totalBytes: number, elapsedMs: number): string => {
	const seconds = elapsedMs / MS_PER_SECOND;
	const throughput = seconds > 0 ? `${(totalBytes / BYTES_PER_MEGABYTE / seconds).toFixed(1)} MB/s` : 'instant';
	const plural = fileCount === 1 ? '' : 's';

	return `Hashed ${fileCount} file${plural} (${megabytesOf(totalBytes)} MB) in ${Math.round(elapsedMs)}ms — ${throughput}`;
};

/** Names the individual file behind a slow pass, which the aggregate cannot. */
export const formatSlowFileHash = (filename: string, bytes: number, elapsedMs: number): string =>
	`Hashing "${filename}" took ${Math.round(elapsedMs)}ms for ${megabytesOf(bytes)} MB`;

/**
 * Reading a file on a cloud-synced volume costs a fixed round trip — measured at ~1.9s per file on
 * Google Drive, regardless of size — because the OS materialises it on demand. That time is spent
 * waiting, so overlapping the reads collapses it, where reading one at a time costs a drop the sum
 * of every round trip.
 *
 * The ceiling is deliberate. Each in-flight hash holds one whole file in memory, so peak use is
 * this many times the largest accepted file — a file count, not a byte budget, so six 200 KB PDFs
 * and six 40 MB drawings are treated alike.
 */
export const HASH_CONCURRENCY = 6;

export interface FileHashed {
	file: File;
	index: number;
	hash: string;
	elapsedMs: number;
}

export interface RunningHashPass {
	/** One promise per file, in drop order. Rejects if that file could not be read. */
	hashes: Promise<string>[];
	/** Settles once every file has been hashed or failed. */
	completed: Promise<void>;
}

/**
 * Starts hashing immediately and hands back a promise per file rather than one for the whole set,
 * so an upload can be queued for every file up front and each waits only on its own bytes. Keeping
 * this off the upload queue's own concurrency is deliberate: a folder of duplicates has to resolve
 * at full speed even while uploads are throttled.
 */
export const startHashPass = (files: File[], onFileHashed?: (result: FileHashed) => void): RunningHashPass => {
	const resolvers: { resolve: (hash: string) => void; reject: (reason: unknown) => void }[] = [];
	const hashes = files.map(
		(_, index) =>
			new Promise<string>((resolve, reject) => {
				resolvers[index] = { resolve, reject };
			}),
	);
	// A task cancelled before it runs never awaits its hash, and an unawaited rejection is noisy.
	hashes.forEach((hash) => hash.catch(() => undefined));

	let nextIndex = 0;
	const hashNextFile = async (): Promise<void> => {
		while (nextIndex < files.length) {
			const index = nextIndex++;
			const file = files[index];
			const startedAt = performance.now();
			try {
				const hash = await sha256Hex(file);
				resolvers[index].resolve(hash);
				onFileHashed?.({ file, index, hash, elapsedMs: performance.now() - startedAt });
			} catch (error) {
				resolvers[index].reject(error);
			}
		}
	};

	const workerCount = Math.min(HASH_CONCURRENCY, files.length);
	const completed = Promise.all(Array.from({ length: workerCount }, hashNextFile)).then(() => undefined);

	return { hashes, completed };
};
